import { Component, For, Show, createEffect } from "solid-js"
import { useAppStore } from "../stores/app"
import { useScenarioStore } from "../stores/scenario"
import type { Agent } from "../types"

// Import SVG icons
import coupleSvg from "../assets/agents/person/couple-mw.svg?raw"
import femaleSvg from "../assets/agents/person/female.svg?raw"
import maleSvg from "../assets/agents/person/male.svg?raw"

// SVG lookup map
const SVG_MAP: Record<string, string> = {
    "male.svg": maleSvg,
    "female.svg": femaleSvg,
    "couple-mw.svg": coupleSvg,
}

interface SimulationOverlayProps {
    visibleAgentIds: Set<string>
}

const SimulationOverlay: Component<SimulationOverlayProps> = (props) => {
    const [scenario] = useScenarioStore();
    const [app, appActions] = useAppStore();
    // C. Show annotations for visible agents, but only expand the selected one
    const visibleAgents = () =>
        scenario.agents
            .filter((a) => props.visibleAgentIds.has(a.id) && (a.name || a.track || a.testimony))
            .sort((a, b) => {
                // Sort by type first
                if (a.type !== b.type) {
                    const typeOrder = {
                        person: 0,
                        car: 1,
                        police: 2,
                        ambulance: 3,
                    }
                    return (typeOrder[a.type] ?? 99) - (typeOrder[b.type] ?? 99)
                }
                // Then sort alphabetically by name within the same type
                const nameA = (a.name || a.label || "").toLowerCase()
                const nameB = (b.name || b.label || "").toLowerCase()
                return nameA.localeCompare(nameB)
            })

    return (
        <>
            {/* Desktop layout - stacked cards */}
            <Show when={visibleAgents().length > 0}>
                <div
                    class="pointer-events-auto absolute top-3 left-14 z-[1000] flex max-h-[calc(100%-24px)] flex-col gap-2 overflow-y-auto"
                    style={{ "max-width": "320px" }}
                >
                    <For each={visibleAgents()}>
                        {(agent) => (
                            <AgentInfoCard
                                agent={agent}
                                isOpen={app.simSelectedAgentId === agent.id}
                                onToggle={() =>
                                    appActions.setSimSelectedAgentId(app.simSelectedAgentId === agent.id ? null : agent.id)
                                }
                            />
                        )}
                    </For>
                </div>
            </Show>
        </>
    )
}

const AgentInfoCard: Component<{
    agent: Agent
    isOpen: boolean
    onToggle: () => void
}> = (props) => {
    let cardRef: HTMLDivElement | undefined

    // Scroll card into view when opened
    createEffect(() => {
        if (props.isOpen && cardRef) {
            cardRef.scrollIntoView({ behavior: "smooth", block: "nearest" })
        }
    })

    return (
        <div
            ref={cardRef}
            class="cursor-pointer rounded-lg border border-gray-200 bg-white/95 px-4 py-2.5 shadow-lg select-none"
            onClick={props.onToggle}
        >
            <div class="flex items-center gap-2">
                <div
                    class="flex h-4 w-4 flex-shrink-0 items-center justify-center overflow-hidden"
                    innerHTML={(() => {
                        // If agent has svg property, use it; otherwise fallback to emoji
                        if (props.agent.svg && SVG_MAP[props.agent.svg]) {
                            return `<div class="sim-svg-icon" style="width: 16px; height: 16px; color: #000000;">${SVG_MAP[props.agent.svg]}</div>`
                        }
                        return `<span style="font-size: 14px; line-height: 1;">${props.agent.emoji}</span>`
                    })()}
                />
                <span class="text-sm font-bold">{props.agent.name || props.agent.label}</span>
                <span class="ml-auto text-xs text-gray-400">{props.isOpen ? "▲" : "▼"}</span>
            </div>

            <Show when={props.isOpen}>
                <div class="mt-3 space-y-3 text-sm text-gray-700">
                    <Show when={props.agent.track}>
                        <div>
                            {/* <span class="font-semibold text-gray-500">Händelseförlopp:</span> */}
                            <For each={props.agent.track!.split("\n").filter((p) => p.trim())}>
                                {(paragraph, i) => <p class={i() > 0 ? "mt-2" : ""}>{paragraph}</p>}
                            </For>
                        </div>
                    </Show>
                    <Show when={props.agent.testimony}>
                        <div>
                            <span class="font-semibold text-gray-800">Vittnesmål</span>
                            <For each={props.agent.testimony!.split("\n").filter((p) => p.trim())}>
                                {(paragraph, i) => <p class={i() > 0 ? "mt-2" : "mt-0.5"}>{paragraph}</p>}
                            </For>
                        </div>
                    </Show>

                    {/* Sources divider - only show if there are any sources */}
                    <Show
                        when={
                            props.agent.sources &&
                            props.agent.sources.length > 0 &&
                            props.agent.sources.some((s) => {
                                const url = s.url.toLowerCase()
                                return (
                                    s.url &&
                                    (url.endsWith(".jpg") ||
                                        url.endsWith(".jpeg") ||
                                        url.endsWith(".webp") ||
                                        s.name)
                                )
                            })
                        }
                    >
                        <div class="border-t border-gray-200 pt-2" />
                    </Show>

                    {/* Agent Images - from sources with image URLs */}
                    <Show
                        when={
                            props.agent.sources &&
                            props.agent.sources.some((s) => {
                                const url = s.url.toLowerCase()
                                return url.endsWith(".jpg") || url.endsWith(".jpeg") || url.endsWith(".webp")
                            })
                        }
                    >
                        <div class="flex flex-wrap gap-2">
                            <For
                                each={props.agent.sources?.filter((s) => {
                                    const url = s.url.toLowerCase()
                                    return url.endsWith(".jpg") || url.endsWith(".jpeg") || url.endsWith(".webp")
                                })}
                            >
                                {(source) => (
                                    <img
                                        src={source.url.startsWith("http") ? source.url : `/people/${source.url}`}
                                        alt={source.name || "Agent image"}
                                        class="h-16 w-16 rounded-md border border-gray-200 object-cover"
                                        onError={(e) => {
                                            e.currentTarget.style.display = "none"
                                        }}
                                    />
                                )}
                            </For>
                        </div>
                    </Show>

                    <Show
                        when={
                            props.agent.sources &&
                            props.agent.sources.some((s) => {
                                const url = s.url.toLowerCase()
                                return (
                                    s.name &&
                                    s.url &&
                                    !(url.endsWith(".jpg") || url.endsWith(".jpeg") || url.endsWith(".webp"))
                                )
                            })
                        }
                    >
                        <div class="pt-2">
                            <span class="text-xs font-semibold text-gray-500">Källor</span>
                            <div class="mt-1 flex flex-wrap gap-2">
                                <For
                                    each={props.agent.sources?.filter((s) => {
                                        const url = s.url.toLowerCase()
                                        return (
                                            s.name &&
                                            s.url &&
                                            !(url.endsWith(".jpg") || url.endsWith(".jpeg") || url.endsWith(".webp"))
                                        )
                                    })}
                                >
                                    {(source) => (
                                        <a
                                            href={source.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            class="text-xs text-blue-600 underline hover:text-blue-700"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            {source.name}
                                        </a>
                                    )}
                                </For>
                            </div>
                        </div>
                    </Show>
                </div>
            </Show>
        </div>
    )
}

export default SimulationOverlay
